import { useRandomId, warnAboutMissingStyles } from '@entur/utils';
import React from 'react';
import { CloseIcon } from '@entur/icons';
import { IconButton } from '@entur/button';
import { Heading4, Heading3, Heading2 } from '@entur/typography';
import classNames from 'classnames';
import { DialogOverlay, DialogContent } from '@reach/dialog';
import { MoveFocusInside } from 'react-focus-lock';
import { Contrast } from '@entur/layout';
function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function (n) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
    }
    return n;
  }, _extends.apply(null, arguments);
}
function _objectWithoutPropertiesLoose(r, e) {
  if (null == r) return {};
  var t = {};
  for (var n in r) if ({}.hasOwnProperty.call(r, n)) {
    if (e.includes(n)) continue;
    t[n] = r[n];
  }
  return t;
}
var _excluded$2 = ["className", "open"];
var ModalOverlay = function ModalOverlay(_ref) {
  var className = _ref.className,
    open = _ref.open,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$2);
  return React.createElement(DialogOverlay, _extends({
    className: classNames('eds-modal__overlay', className),
    isOpen: open
  }, rest));
};
var _excluded$1 = ["children", "className", "size", "title", "align"];
var headingsMap = {
  extraSmall: Heading4,
  small: Heading3,
  medium: Heading2,
  large: Heading2,
  extraLarge: Heading2
};
var ModalContent = function ModalContent(_ref) {
  var children = _ref.children,
    className = _ref.className,
    size = _ref.size,
    title = _ref.title,
    _ref$align = _ref.align,
    align = _ref$align === void 0 ? 'start' : _ref$align,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$1);
  var Heading = headingsMap[size] || Heading2;
  var randomId = useRandomId('eds-modal');
  return React.createElement(DialogContent, _extends({
    className: classNames('eds-modal__content', "eds-modal__content--size-" + size, "eds-modal__content--align-" + align, className),
    "aria-labelledby": randomId
  }, rest), title && React.createElement(Heading, {
    margin: "bottom",
    as: "h2",
    id: randomId
  }, title), children);
};
var _excluded = ["children", "closeLabel", "initialFocusRef", "open", "onDismiss", "size", "align", "title", "closeOnClickOutside"];
var Modal = function Modal(_ref) {
  var children = _ref.children,
    _ref$closeLabel = _ref.closeLabel,
    closeLabel = _ref$closeLabel === void 0 ? 'Lukk' : _ref$closeLabel,
    initialFocusRef = _ref.initialFocusRef,
    open = _ref.open,
    onDismiss = _ref.onDismiss,
    size = _ref.size,
    _ref$align = _ref.align,
    align = _ref$align === void 0 ? 'start' : _ref$align,
    title = _ref.title,
    _ref$closeOnClickOuts = _ref.closeOnClickOutside,
    closeOnClickOutside = _ref$closeOnClickOuts === void 0 ? true : _ref$closeOnClickOuts,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  var randomId = useRandomId('eds-modal');
  var Heading = headingsMap[size] || Heading2;
  var showCloseButton = ['medium', 'large', 'extraLarge'].includes(size);
  var handleOnDismiss;
  if (onDismiss && closeOnClickOutside) {
    handleOnDismiss = onDismiss;
  }
  return React.createElement(ModalOverlay, {
    open: open,
    onDismiss: handleOnDismiss,
    initialFocusRef: initialFocusRef
  }, React.createElement(ModalContent, _extends({
    size: size,
    align: align
  }, rest), showCloseButton && React.createElement(IconButton, {
    className: "eds-modal__close",
    "aria-label": closeLabel,
    onClick: onDismiss,
    type: "button"
  }, React.createElement(CloseIcon, null)), title && React.createElement(Heading, {
    margin: "bottom",
    as: "h2",
    id: randomId
  }, title), children));
};
var Drawer = function Drawer(_ref) {
  var children = _ref.children,
    className = _ref.className,
    _ref$closeLabel = _ref.closeLabel,
    closeLabel = _ref$closeLabel === void 0 ? 'Lukk skuff' : _ref$closeLabel,
    _ref$contrast = _ref.contrast,
    contrast = _ref$contrast === void 0 ? false : _ref$contrast,
    _ref$open = _ref.open,
    open = _ref$open === void 0 ? true : _ref$open,
    onDismiss = _ref.onDismiss,
    title = _ref.title,
    style = _ref.style,
    _ref$overlay = _ref.overlay,
    overlay = _ref$overlay === void 0 ? false : _ref$overlay;
  var titleId = useRandomId('eds-drawer');
  if (!open) {
    return null;
  }
  var handleKeyDown = function handleKeyDown(e) {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onDismiss();
    }
  };
  var Wrapper = contrast ? Contrast : React.Fragment;
  var ContentContainer = overlay ? DialogContent : 'div';
  return React.createElement(ConditionalWrapper, {
    condition: overlay,
    wrapper: function wrapper(children) {
      return React.createElement(ModalOverlay, {
        open: open,
        onDismiss: onDismiss
      }, children);
    }
  }, React.createElement(Wrapper, null, React.createElement(ContentContainer, {
    "aria-labelledby": titleId,
    className: classNames('eds-drawer', className),
    onKeyDown: handleKeyDown,
    style: style
  }, React.createElement(MoveFocusInside, null, React.createElement(IconButton, {
    className: "eds-drawer__close-button",
    onClick: onDismiss,
    type: "button",
    "aria-label": closeLabel
  }, React.createElement(CloseIcon, {
    "aria-hidden": true
  })), React.createElement("div", {
    className: "eds-drawer__content"
  }, React.createElement(Heading3, {
    as: "h2",
    id: titleId
  }, title), children)))));
};
var ConditionalWrapper = function ConditionalWrapper(_ref2) {
  var condition = _ref2.condition,
    wrapper = _ref2.wrapper,
    children = _ref2.children;
  return condition ? wrapper(children) : children;
};
warnAboutMissingStyles('modal', 'icons', 'typography', 'a11y', 'button');
export { Drawer, Modal, ModalContent, ModalOverlay, headingsMap };